/**
 * Koneko UI visibility — who sees which panels and actions.
 *
 * View-as uses these caps for the previewed username (not the signed-in admin).
 * Merge order: member/admin defaults → shipped grants (deploy) → server API.
 * localStorage applies only when the server map has not been loaded (offline dev).
 */
import {
  canonicalizeKonekoUsername,
  konekoAAdminUsernames,
  listKonekoAPlanningMemberKeys,
  resolveKonekoAPlanningUsernameKey,
} from "@/config/konekoAConfig";

export const KONEKO_VISIBILITY_STORAGE_KEY =
  "nekohr_koneko_visibility_overrides";

/** @typedef {Record<string, boolean>} KonekoCapabilityMap */

export const KONEKO_CAP = {
  dashboardProjectSnapshot: "dashboard.project_snapshot",
  dashboardMemberBreakdownAll: "dashboard.member_breakdown_all",
  konekoMyBudget: "konekoA.my_budget",
  konekoProjectSnapshot: "konekoA.project_snapshot",
  konekoCrossComparison: "konekoA.cross_comparison",
  konekoOrgBudgetEditor: "konekoA.org_budget_editor",
  konekoTeamPersonalBudgets: "konekoA.team_personal_budgets",
  konekoMemberBreakdownAll: "konekoA.member_breakdown_all",
  konekoReassignTimesheet: "konekoA.reassign_timesheet",
  navKonekoAFull: "nav.koneko_a_full",
  navPayroll: "nav.payroll",
  canUseViewAs: "app.can_use_view_as",
};

const ALL_CAPS = Object.values(KONEKO_CAP);

/** Budget-editor style access (matches access matrix for victor / kai). */
const KONEKO_BUDGET_EDITOR_GRANT = {
  [KONEKO_CAP.konekoMyBudget]: true,
  [KONEKO_CAP.konekoProjectSnapshot]: true,
  [KONEKO_CAP.konekoCrossComparison]: true,
  [KONEKO_CAP.konekoOrgBudgetEditor]: true,
  [KONEKO_CAP.konekoTeamPersonalBudgets]: true,
  [KONEKO_CAP.konekoMemberBreakdownAll]: true,
  [KONEKO_CAP.konekoReassignTimesheet]: true,
  [KONEKO_CAP.dashboardProjectSnapshot]: true,
  [KONEKO_CAP.dashboardMemberBreakdownAll]: true,
};

/**
 * Shipped with the SPA — applies on every device (production included).
 * Update when access matrix changes until server PUT is deployed and synced.
 */
/** Payroll month matrix — Liam + Tokiko only (also editable in Koneko access). */
const PAYROLL_NAV_GRANT = {
  [KONEKO_CAP.navPayroll]: true,
};

export const KONEKO_VISIBILITY_SHIPPED_GRANTS = {
  victor: KONEKO_BUDGET_EDITOR_GRANT,
  kai: KONEKO_BUDGET_EDITOR_GRANT,
  liam_maneki: PAYROLL_NAV_GRANT,
  tokiko: PAYROLL_NAV_GRANT,
};

/** @type {Record<string, Record<string, boolean>>} */
let serverVisibilityOverrides = {};
let serverVisibilityLoaded = false;

const ADMIN_DEFAULTS = ALL_CAPS.reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

const MEMBER_DEFAULTS = {
  [KONEKO_CAP.dashboardProjectSnapshot]: false,
  [KONEKO_CAP.dashboardMemberBreakdownAll]: false,
  [KONEKO_CAP.konekoMyBudget]: true,
  [KONEKO_CAP.konekoProjectSnapshot]: false,
  [KONEKO_CAP.konekoCrossComparison]: false,
  [KONEKO_CAP.konekoOrgBudgetEditor]: false,
  [KONEKO_CAP.konekoTeamPersonalBudgets]: false,
  [KONEKO_CAP.konekoMemberBreakdownAll]: false,
  [KONEKO_CAP.konekoReassignTimesheet]: false,
  [KONEKO_CAP.navKonekoAFull]: false,
  [KONEKO_CAP.canUseViewAs]: false,
};

export const KONEKO_CAP_LABELS = {
  [KONEKO_CAP.dashboardProjectSnapshot]: "Dashboard — project snapshot",
  [KONEKO_CAP.dashboardMemberBreakdownAll]: "Dashboard — all members breakdown",
  [KONEKO_CAP.konekoMyBudget]: "Koneko A — my budget editor",
  [KONEKO_CAP.konekoProjectSnapshot]: "Koneko A — project budget snapshot",
  [KONEKO_CAP.konekoCrossComparison]: "Koneko A — cross comparison",
  [KONEKO_CAP.konekoOrgBudgetEditor]: "Koneko A — org budget editor",
  [KONEKO_CAP.konekoTeamPersonalBudgets]: "Koneko A — team personal budgets",
  [KONEKO_CAP.konekoMemberBreakdownAll]: "Koneko A — all members breakdown",
  [KONEKO_CAP.konekoReassignTimesheet]: "Reassign timesheet lines",
  [KONEKO_CAP.navKonekoAFull]: "Nav — full Koneko A page",
  [KONEKO_CAP.navPayroll]: "Nav — payroll month matrix",
  [KONEKO_CAP.canUseViewAs]: "Use view-as preview (admins only)",
};

function isPlanningAdminUsername(username) {
  const key = canonicalizeKonekoUsername(username);
  if (!key) return false;
  return konekoAAdminUsernames.some((a) => String(a).toLowerCase() === key);
}

/** Map any username input to the roster key used in the access matrix. */
export function rosterKeyForVisibilityOverrides(rawKey) {
  const t = String(rawKey || "").trim();
  if (!t) return "";
  return (
    resolveKonekoAPlanningUsernameKey(t) || canonicalizeKonekoUsername(t) || t
  );
}

function normalizeOverridesMap(all) {
  if (!all || typeof all !== "object" || Array.isArray(all)) return {};
  const out = {};
  for (const [rawKey, perUser] of Object.entries(all)) {
    const key = rosterKeyForVisibilityOverrides(rawKey);
    if (
      !key ||
      !perUser ||
      typeof perUser !== "object" ||
      Array.isArray(perUser)
    ) {
      continue;
    }
    if (!out[key]) out[key] = {};
    for (const [cap, val] of Object.entries(perUser)) {
      if (typeof val === "boolean") out[key][cap] = val;
    }
  }
  return out;
}

function readLocalOverrides() {
  try {
    const raw = localStorage.getItem(KONEKO_VISIBILITY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const normalized = normalizeOverridesMap(parsed);
    if (JSON.stringify(normalized) !== JSON.stringify(parsed)) {
      saveKonekoVisibilityOverridesLocalOnly(normalized);
    }
    return normalized;
  } catch {
    return {};
  }
}

function applyGrantLayer(base, username, layer) {
  if (!layer || typeof layer !== "object") return;
  const perUser = layer[username];
  if (!perUser || typeof perUser !== "object") return;
  ALL_CAPS.forEach((cap) => {
    if (typeof perUser[cap] === "boolean") base[cap] = perUser[cap];
  });
}

export function readServerVisibilityOverrides() {
  return serverVisibilityOverrides;
}

export function isServerVisibilityLoaded() {
  return serverVisibilityLoaded;
}

/**
 * Load overrides from `api/koneko/v1/access/` (budgets service or nekoapi).
 * @returns {Promise<boolean>} true when a JSON map was applied
 */
export async function loadServerVisibilityOverrides(fetchFn) {
  const fetcher =
    fetchFn ||
    (async () => {
      const mod = await import("@/services/konekoAccessApi");
      return mod.fetchKonekoAccessOverrides();
    });
  try {
    const data = await fetcher();
    if (data && typeof data === "object") {
      serverVisibilityOverrides = normalizeOverridesMap(data);
      serverVisibilityLoaded = true;
      return true;
    }
  } catch {
    /* ignore */
  }
  serverVisibilityLoaded = true;
  return false;
}

export async function persistServerVisibilityOverrides(
  overridesByUser,
  {replace = false} = {}
) {
  const mod = await import("@/services/konekoAccessApi");
  const normalized = normalizeOverridesMap(overridesByUser || {});
  const result = await mod.putKonekoAccessOverrides(normalized, {replace});
  if (result.ok) {
    serverVisibilityOverrides = replace
      ? normalized
      : normalizeOverridesMap({
          ...serverVisibilityOverrides,
          ...normalized,
        });
    serverVisibilityLoaded = true;
  }
  return result;
}

/** Overrides used for editing/export — server map when loaded, else local. */
export function readKonekoVisibilityOverrides() {
  if (serverVisibilityLoaded) {
    return {...serverVisibilityOverrides};
  }
  return readLocalOverrides();
}

function saveKonekoVisibilityOverridesLocalOnly(overridesByUser) {
  try {
    localStorage.setItem(
      KONEKO_VISIBILITY_STORAGE_KEY,
      JSON.stringify(normalizeOverridesMap(overridesByUser || {}))
    );
  } catch {
    /* ignore */
  }
}

function baseDefaultsForUsername(username) {
  return isPlanningAdminUsername(username) ? ADMIN_DEFAULTS : MEMBER_DEFAULTS;
}

/**
 * Effective capabilities for a roster username (canonical key).
 * @param {string} username
 * @returns {KonekoCapabilityMap}
 */
function shouldApplyLocalVisibilityOverrides() {
  if (typeof window === "undefined") return true;
  // Stale localStorage from admin testing must not block My budget on deployed hosts.
  const host = String(window.location.hostname || "");
  if (
    /nekohr\.nekoapi\.xyz/i.test(host) ||
    /(^|\.)neko-logic\.com$/i.test(host)
  ) {
    return false;
  }
  return true;
}

export function getKonekoCapabilities(username) {
  const key = rosterKeyForVisibilityOverrides(username);
  const base = {...baseDefaultsForUsername(key)};
  applyGrantLayer(base, key, KONEKO_VISIBILITY_SHIPPED_GRANTS);
  if (serverVisibilityLoaded) {
    applyGrantLayer(base, key, serverVisibilityOverrides);
  } else if (shouldApplyLocalVisibilityOverrides()) {
    applyGrantLayer(base, key, readLocalOverrides());
  }
  return base;
}

export function hasKonekoCapability(username, cap) {
  const caps = getKonekoCapabilities(username);
  return Boolean(caps[cap]);
}

/**
 * Persist access map — server first; mirror to localStorage on success.
 * @returns {Promise<{ ok: boolean, status: number, detail: string }>}
 */
export async function saveKonekoVisibilityOverrides(
  overridesByUser,
  {replace = false} = {}
) {
  const normalized = normalizeOverridesMap(overridesByUser || {});
  const remote = await persistServerVisibilityOverrides(normalized, {replace});
  if (remote.ok) {
    saveKonekoVisibilityOverridesLocalOnly(normalized);
  } else {
    saveKonekoVisibilityOverridesLocalOnly(normalized);
  }
  return remote;
}

export function countVisibilityOverrides() {
  const all = readKonekoVisibilityOverrides();
  let members = 0;
  let caps = 0;
  Object.values(all).forEach((perUser) => {
    if (!perUser || typeof perUser !== "object") return;
    const keys = Object.keys(perUser);
    if (!keys.length) return;
    members += 1;
    caps += keys.length;
  });
  return {members, caps};
}

export function exportVisibilityOverridesJson() {
  return JSON.stringify(readKonekoVisibilityOverrides(), null, 2);
}

/**
 * Merge imported overrides into storage (does not clear rows missing from file).
 * @returns {{ ok: boolean, members: number, error?: string }}
 */
export async function importVisibilityOverridesJson(
  text,
  {replace = false} = {}
) {
  try {
    const parsed = JSON.parse(String(text || ""));
    const incoming = normalizeOverridesMap(parsed);
    const merged = replace ? incoming : {...readKonekoVisibilityOverrides()};
    if (!replace) {
      Object.entries(incoming).forEach(([key, perUser]) => {
        merged[key] = {...(merged[key] || {}), ...perUser};
      });
    }
    const remote = await persistServerVisibilityOverrides(merged, {replace});
    if (remote.ok) {
      saveKonekoVisibilityOverridesLocalOnly(merged);
    } else {
      saveKonekoVisibilityOverridesLocalOnly(merged);
    }
    if (!remote.ok) {
      return {
        ok: true,
        members: Object.keys(incoming).length,
        warn: remote.detail || "Saved locally; server sync failed.",
      };
    }
    return {ok: true, members: Object.keys(incoming).length};
  } catch (e) {
    return {ok: false, members: 0, error: (e && e.message) || "invalid JSON"};
  }
}

export function clearKonekoVisibilityOverrides() {
  try {
    localStorage.removeItem(KONEKO_VISIBILITY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  serverVisibilityOverrides = {};
}

export function listKonekoVisibilityRosterKeys() {
  return listKonekoAPlanningMemberKeys();
}

/** Matrix columns for the Koneko A access admin (panels, nav, related dashboard). */
export const KONEKO_A_ACCESS_GROUPS = [
  {
    id: "nav",
    label: "Navigation",
    items: [
      {
        cap: KONEKO_CAP.navKonekoAFull,
        label: "Full Koneko A",
        hint: "Nav link to the full Koneko A page (all permitted panels).",
      },
      {
        cap: KONEKO_CAP.konekoMyBudget,
        label: "My budget",
        hint: "Nav shortcut and personal budget editor panel.",
      },
      {
        cap: KONEKO_CAP.navPayroll,
        label: "Payroll",
        hint: "Month matrix of hours by staff member and project.",
      },
    ],
  },
  {
    id: "panels",
    label: "Koneko A panels",
    items: [
      {
        cap: KONEKO_CAP.konekoProjectSnapshot,
        label: "Project snapshot",
        hint: "Org-wide project budget snapshot.",
      },
      {
        cap: KONEKO_CAP.konekoCrossComparison,
        label: "Cross comparison",
        hint: "Compare projects and members side by side.",
      },
      {
        cap: KONEKO_CAP.konekoOrgBudgetEditor,
        label: "Org budget editor",
        hint: "Edit org / project budget bands for the month.",
      },
      {
        cap: KONEKO_CAP.konekoTeamPersonalBudgets,
        label: "Team personal budgets",
        hint: "Admin table of everyone’s personal caps.",
      },
      {
        cap: KONEKO_CAP.konekoMemberBreakdownAll,
        label: "All members breakdown",
        hint: "When off, only their own member row is shown.",
      },
    ],
  },
  {
    id: "actions",
    label: "Actions",
    items: [
      {
        cap: KONEKO_CAP.konekoReassignTimesheet,
        label: "Reassign timesheet",
        hint: "Move timesheet lines between projects.",
      },
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard (when using View as)",
    items: [
      {
        cap: KONEKO_CAP.dashboardProjectSnapshot,
        label: "Project snapshot",
        hint: "Project Budget Snapshot section on Dashboard.",
      },
      {
        cap: KONEKO_CAP.dashboardMemberBreakdownAll,
        label: "All members",
        hint: "When off, Dashboard shows only their member row.",
      },
    ],
  },
];

/** Flat cap keys shown on the Koneko A access admin matrix. */
export function listKonekoAAccessCapKeys() {
  const keys = [];
  KONEKO_A_ACCESS_GROUPS.forEach((group) => {
    group.items.forEach((item) => keys.push(item.cap));
  });
  return keys;
}

export function clearMemberKonekoVisibilityOverrides(memberKey) {
  const key = rosterKeyForVisibilityOverrides(memberKey);
  if (!key) return;
  const all = readLocalOverrides();
  if (all[key]) {
    delete all[key];
    saveKonekoVisibilityOverridesLocalOnly(all);
  }
  if (serverVisibilityOverrides[key]) {
    const next = {...serverVisibilityOverrides};
    delete next[key];
    serverVisibilityOverrides = next;
  }
}

export function isKonekoCapabilityOverridden(memberKey, cap) {
  const key = rosterKeyForVisibilityOverrides(memberKey);
  const layer = serverVisibilityLoaded
    ? serverVisibilityOverrides
    : readLocalOverrides();
  const perUser = layer[key];
  if (perUser && typeof perUser[cap] === "boolean") return true;
  const shipped = KONEKO_VISIBILITY_SHIPPED_GRANTS[key];
  return Boolean(shipped && typeof shipped[cap] === "boolean");
}
